import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import { ERP_NAMES } from '@/domain/companies/helpers';
import { currentConnectedERPselector } from '@/domain/settings/settingsSelector';

import { AgGridReact } from 'ag-grid-react';
import {
  ModuleRegistry,
  ColumnHoverModule,
  TooltipModule,
  CellStyleModule,
  LocaleModule,
  RowStyleModule,
  ExternalFilterModule,
  ColumnAutoSizeModule,
  RenderApiModule,
} from 'ag-grid-community';
import type {
  ColDef,
  GridApi,
  GridReadyEvent,
  RowClassParams,
  RowHeightParams,
} from 'ag-grid-community';
import { ServerSideRowModelApiModule, ServerSideRowModelModule, SparklinesModule } from 'ag-grid-enterprise';
import { AgChartsEnterpriseModule } from 'ag-charts-enterprise';

import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import LinearProgress from '@mui/material/LinearProgress';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';

import NoRowsOverlayComponent from '@/pages/company/DashboardRoute/pages/VendorExpensesRoute/components/NoRowsOverlayComponent/NoRowsOverlayComponent';
import { addHeaderTooltipPropsForColDefs } from '@/components/AgGrid/helpers';
import AgGridColumnHeader from '@/components/AgGrid/components/ColumnHeader';
import AmountCellRenderer from '@/pages/company/DashboardRoute/pages/VendorExpensesRoute/components/AmountCellRenderer';
import { useVendorExpensesContext } from '@/pages/company/DashboardRoute/pages/VendorExpensesRoute/vendorExpensesContextProvider';
import useAgGridProps from '@/components/AgGrid/Dashboard/VendorExpenses/useAgGridProps';
import useFetchColumnDefs from '@/components/AgGrid/Dashboard/VendorExpenses/useFetchColumnDefs';
import useAgGridColumnTypes from '@/components/AgGrid/Dashboard/VendorExpenses/useAgGridColumnTypes';
import useAgGridTheme from '@/components/AgGrid/hooks/useAgGridTheme';

// inject order important for styles
import AgGridGlobalStyles from '@/components/AgGrid/components/AgGridGlobalStyles';
import AgGridStyles from '@/components/AgGrid/Dashboard/VendorExpenses/AgGridStyles';
import useAgGridRtlMethods from '@/components/AgGrid/hooks/useAgGridRtlMethods';
import useAgGridTranslations from '@/components/AgGrid/hooks/useAgGridTranslations';
import useServerDatasource, {
  type VendorExpenseRow,
} from '@/pages/company/DashboardRoute/pages/VendorExpensesRoute/components/AgGrid/useServerDatasource';
import useGridFilters from '@/pages/company/DashboardRoute/pages/VendorExpensesRoute/components/AgGrid/useGridFilters';

const PRIORITY_ERP_KEYS = [ERP_NAMES.priorityPro, ERP_NAMES.priorityProSQL] as const;

ModuleRegistry.registerModules([
  SparklinesModule.with(AgChartsEnterpriseModule),
  ServerSideRowModelModule,
  ColumnHoverModule,
  LocaleModule,
  RowStyleModule,
  ExternalFilterModule,
  TooltipModule,
  CellStyleModule,
  ColumnAutoSizeModule,
  ServerSideRowModelApiModule,
  RenderApiModule,
]);

const VendorExpensesGrid = () => {
  const { formatMessage } = useIntl();
  const gridApiRef = useRef<GridApi<VendorExpenseRow> | null>(null);
  const agGridTheme = useAgGridTheme();
  const { enableRtl } = useAgGridRtlMethods();
  const { getLocaleText } = useAgGridTranslations();

  const {
    fieldProps: {
      period: { value: periodFilterValue },
      topVendors: { value: topVendorsFilterValue },
      currencies: { value: currenciesFilterValue },
      vendors: { value: vendorsFilterValue },
    },
    detailsIsExpanded,
    handleChangeDetailsIsExpanded,
  } = useVendorExpensesContext();

  const currentErpKey = useSelector(currentConnectedERPselector);
  const isPriorityErp = PRIORITY_ERP_KEYS.includes(currentErpKey);

  const agGridDefaultProps = useAgGridProps();
  const columnTypes = useAgGridColumnTypes({ isPriorityErp });

  const agGridContext = useMemo(
    () => ({
      detailsIsExpanded,
      isPriorityErp,
    }),
    [detailsIsExpanded, isPriorityErp],
  );

  const filters = useMemo(
    () => ({
      period: periodFilterValue,
      topVendors: topVendorsFilterValue,
      currencies: currenciesFilterValue,
      vendors: vendorsFilterValue,
    }),
    [periodFilterValue, topVendorsFilterValue, currenciesFilterValue, vendorsFilterValue],
  );

  const { rawColumnDefs, fetchColumnDefs, rawColumnDefsIsFetched } = useFetchColumnDefs({
    periodValue: periodFilterValue,
  });

  // fetchColumnDefs closes over the current periodValue prop; no arg needed.
  const handlePeriodChange = useCallback(async () => {
    try {
      await fetchColumnDefs();
      gridApiRef.current?.onFilterChanged();
    } catch (error) {
      console.error('Error updating columns for period change:', error);
    }
  }, [fetchColumnDefs]);

  const handleFiltersChange = useCallback(() => {
    gridApiRef.current?.onFilterChanged();
  }, []);

  useGridFilters(filters, {
    onPeriodChange: handlePeriodChange,
    onFilterChange: handleFiltersChange,
  });

  const columnDefs = useMemo<ColDef<VendorExpenseRow>[]>(
    () => addHeaderTooltipPropsForColDefs(rawColumnDefs),
    [rawColumnDefs],
  );

  const components = useMemo(
    () => ({
      agColumnHeader: AgGridColumnHeader,
      amountCellRenderer: AmountCellRenderer,
    }),
    [],
  );

  const serverDatasource = useServerDatasource(filters);

  const onGridReady = useCallback(({ api }: GridReadyEvent<VendorExpenseRow>) => {
    gridApiRef.current = api;
  }, []);

  const autoGroupColumnDef = useMemo<ColDef<VendorExpenseRow> | undefined>(() => {
    const groupColumnDef = columnDefs.find((colDef) => colDef.field === 'vendor_name');

    if (groupColumnDef) {
      const side: 'left' | 'right' = enableRtl ? 'right' : 'left';
      return {
        ...groupColumnDef,
        pinned: side,
        lockPosition: side,
        lockPinned: true,
        headerComponentParams: {
          enablePinning: false,
        },
        hide: false,
        suppressSizeToFit: false,
        tooltipField: 'vendor_name',
        minWidth: 300,
      };
    }
    return undefined;
  }, [columnDefs, enableRtl]);

  // Rows with more than 1 child are the only ones treated as a group and can be expanded/collapsed,
  // except when there is only 1 child with a different currency.
  const isServerSideGroup = useCallback(
    (dataItem: VendorExpenseRow) =>
      !!dataItem.underlings &&
      (dataItem.underlings.length > 1 ||
        (dataItem.underlings.length === 1 && dataItem.currency !== dataItem.underlings[0].currency)),
    [],
  );

  const getServerSideGroupKey = useCallback((dataItem: VendorExpenseRow) => String(dataItem.vendor_id), []);

  const getRowHeight = useCallback((params: RowHeightParams<VendorExpenseRow>) => {
    if (params.node.level === 1) {
      const baseHeight = 62;
      return params.data?.__meta?.isLast ? baseHeight + 10 : baseHeight;
    }
    return undefined;
  }, []);

  const getRowClass = useCallback((params: RowClassParams<VendorExpenseRow>) => {
    if (params.node.level !== 1) return '';

    const classes: string[] = [];
    const meta = params.data?.__meta;

    if (meta?.isFirst) classes.push('first-level-1');
    if (meta?.isLast) classes.push('last-level-1');
    if (meta?.isOnly) classes.push('only-level-1');

    return classes.join(' ');
  }, []);

  const isExternalFilterPresent = useCallback(() => true, []);
  const doesExternalFilterPass = useCallback(() => true, []);

  useEffect(() => {
    if (gridApiRef.current) {
      const columns = columnDefs
        .filter((colDef) => colDef.type === 'totals')
        .map((colDef) => colDef.field)
        .filter((field): field is string => field !== undefined);

      gridApiRef.current.refreshCells({
        columns,
        force: true,
      });
    }
  }, [detailsIsExpanded, columnDefs]);

  if (!rawColumnDefsIsFetched) {
    return <LinearProgress />;
  }

  return (
    <Box className="ag-theme-dokka" display="flex" flexDirection="column" height="100%">
      <AgGridGlobalStyles />
      <AgGridStyles />

      <Typography variant="h6">
        {formatMessage({
          id: 'company.dashboard.vendorExpenses.grid.title',
          defaultMessage: 'Total vendor charges as recorded in the accounting system.',
        })}
      </Typography>

      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <Typography variant="subtitle1" sx={{ color: 'text.secondary', fontWeight: '500', mr: 1 }}>
          {formatMessage({
            id: 'company.dashboard.vendorExpenses.grid.description',
            defaultMessage:
              'Monthly invoice totals per vendor, based on invoice date, as recorded in the accounting system.',
          })}
        </Typography>
        {!isPriorityErp && (
          <FormControlLabel
            sx={{ flex: '0 0 auto', ml: 'auto' }}
            control={<Switch size="small" checked={detailsIsExpanded} onChange={handleChangeDetailsIsExpanded} />}
            label={formatMessage({ id: 'labels.expandDetails', defaultMessage: 'Expand details' })}
          />
        )}
      </Box>

      <AgGridReact
        {...agGridDefaultProps}
        theme={agGridTheme}
        onGridReady={onGridReady}
        columnDefs={columnDefs}
        autoGroupColumnDef={autoGroupColumnDef}
        serverSideDatasource={serverDatasource}
        isServerSideGroup={isServerSideGroup}
        getServerSideGroupKey={getServerSideGroupKey}
        getRowHeight={getRowHeight}
        getRowClass={getRowClass}
        noRowsOverlayComponent={NoRowsOverlayComponent}
        columnTypes={columnTypes}
        components={components}
        context={agGridContext}
        enableRtl={enableRtl}
        getLocaleText={getLocaleText}
        isExternalFilterPresent={isExternalFilterPresent}
        doesExternalFilterPass={doesExternalFilterPass}
      />
    </Box>
  );
};

export default VendorExpensesGrid;
